import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { compact, size } from 'lodash';
import cn from 'classnames';
import {
  faArrowUpRightFromSquare,
  faBooks,
  faExternalLinkSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  Dropdown,
  ExternalLink,
  getPassthroughLink,
  SearchOption,
} from '@app/components';
import { useRecordSummaryContext } from '@app/contexts';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { openUrlInNewTab, processElectronicAccess } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Feature, ShapeWithClassName } from '@app/constants';

import './AccessOptions.scss';

const cnBem = createBemBlockBuilder(['accessOptions']);

export const AccessOptions: FC<ShapeWithClassName> = ({ className }) => {
  const {
    availability = { isRequestable: true },
    ILLLinks = [],
    links,
    instance,
    isAvailabilityLoadingForTheFirstTime,
    toggleIsPlaceHoldModalOpen,
  } = useRecordSummaryContext();
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();
  const { isRequestable } = availability;
  const { $t } = useIntl();
  const placeHoldText = selectConfiguredOption(
    $t(sharedMessages.requestItem),
    $t(sharedMessages.placeHold)
  );
  const { isActive: isPlaceHoldFlagActive } = useFeatureFlag({
    name: Feature.placeHold,
    isActiveIfNotFound: true,
  });
  const {
    isActive: isAeonFlagActive,
    config: { url: aeonUrl, linkText: aeonLinkText },
  } = useFeatureFlag({ name: Feature.AEON });
  const {
    isActive: isPassthroughLinkFlagActive,
    config: { consortiaName, consortiaLink },
  } = useFeatureFlag({ name: Feature.passthroughlink });

  const accessLinks = useMemo(
    () => processElectronicAccess(instance.electronicAccess),
    [instance.electronicAccess]
  );

  const isEveryItemInSpecialLocation =
    isAeonFlagActive && availability.isEveryItemInSpecialLocation;
  const hasPlaceHold =
    isRequestable && isPlaceHoldFlagActive && !isEveryItemInSpecialLocation;
  const hasAeon = isAeonFlagActive && availability.isInSpecialCollection;
  const hasPassthroughLink =
    isPassthroughLinkFlagActive && consortiaName && consortiaLink;

  const handleDropdownChange = useCallback(
    ({ selectedItem: { value } }) => {
      const isPlaceHold = value === 'placeHold';

      if (isPlaceHold) {
        toggleIsPlaceHoldModalOpen();

        return;
      }

      if (value) {
        openUrlInNewTab(value);
      }
    },
    [toggleIsPlaceHoldModalOpen]
  );

  const ILLLinkItems = useMemo(
    () =>
      isEveryItemInSpecialLocation
        ? []
        : ILLLinks?.map(({ link, label }) => ({
            label,
            disabled: isAvailabilityLoadingForTheFirstTime,
            icon: faBooks,
            value: link,
          })),
    [
      ILLLinks,
      isAvailabilityLoadingForTheFirstTime,
      isEveryItemInSpecialLocation,
    ]
  );

  const dropdownItems = useMemo(
    () =>
      compact([
        hasPlaceHold && {
          label: placeHoldText,
          disabled: isAvailabilityLoadingForTheFirstTime,
          icon: faBooks,
          value: 'placeHold',
        },
        hasPassthroughLink && {
          label: $t(
            {
              id: 'resultsList.findThisAt',
              defaultMessage: 'Find this at {consortiaName}',
            },
            { consortiaName }
          ),
          disabled: isAvailabilityLoadingForTheFirstTime,
          icon: faBooks,
          value: getPassthroughLink({
            consortiaLink,
            option: SearchOption.title,
            query: instance.title,
          }),
        },
        hasAeon && {
          label: aeonLinkText,
          disabled: isAvailabilityLoadingForTheFirstTime,
          icon: faBooks,
          value: `${aeonUrl}${instance.id}`,
        },
        ...ILLLinkItems,
        ...accessLinks.map(link => ({
          label: link.linkText,
          icon: faExternalLinkSquare,
          value: link.uri,
        })),
      ]).map((item, index) => ({ ...item, index })),
    [
      hasPlaceHold,
      placeHoldText,
      isAvailabilityLoadingForTheFirstTime,
      hasPassthroughLink,
      $t,
      consortiaName,
      consortiaLink,
      instance.title,
      instance.id,
      hasAeon,
      aeonLinkText,
      aeonUrl,
      ILLLinkItems,
      accessLinks,
    ]
  );

  const renderAccessButton = () => {
    const dropdownItemsAmount = size(dropdownItems);

    if (dropdownItemsAmount === 0) {
      return null;
    }

    if (dropdownItemsAmount === 1) {
      if (hasPlaceHold) {
        return (
          <Button
            disabled={isAvailabilityLoadingForTheFirstTime}
            onClick={toggleIsPlaceHoldModalOpen}
          >
            {placeHoldText}
          </Button>
        );
      }

      const { uri, linkText } = accessLinks[0] || {};

      if (linkText && uri) {
        return (
          <ExternalLink className={cnBem('__electronicAccessLink')} href={uri}>
            <span>{linkText}</span>
          </ExternalLink>
        );
      }
    }

    return (
      <div className={cnBem('__dropdown')}>
        <Dropdown
          id="access-options-dropdown"
          placeholder={$t({
            id: 'resultsList.accessOptions',
            defaultMessage: 'Access options',
          })}
          items={dropdownItems}
          onSelectedItemChange={handleDropdownChange}
        />
      </div>
    );
  };

  const hathiTrustUrl = instance?.oclcPermalinks?.[0]?.hathiTrust;

  return (
    <div className={cn(cnBem(), className)}>
      {renderAccessButton()}
      {hathiTrustUrl && (
        <ExternalLink
          className={cnBem('__linkStandardButton')}
          href={hathiTrustUrl}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          {$t({
            id: 'instanceDetails.HathiTrustLink',
            defaultMessage: 'HathiTrust full text',
          })}
        </ExternalLink>
      )}
      {links}
    </div>
  );
};
